import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { LicenseInfo } from "@mui/x-license";
import { initializeIcons } from "@fluentui/react";
import { ClerkProvider } from "@clerk/clerk-react";

// React Redux - Import store first
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import store, { persistor } from "./state/store";

// Import components that depend on store after store is imported
import { AuthProvider } from "./auth/AuthProvider";
import { AccessManifestProvider } from "./auth/AccessManifestProvider";
import { FeatureFlagProvider } from "./features";
import AppRoutes from "./Routes";

import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";

import "./hotjar.js";
import "./index.css";
import { initGA } from "./utils/analytics";
import { initAttributionTracking } from "./utils/attribution";
import { checkAndHandleVersionChange } from "./utils/versionCheck";
import VersionUpdateSplash from "./components/VersionUpdateSplash";

import { FluentProvider, makeStyles, PartialTheme } from "@fluentui/react-components";

const MUX_LICENSE_KEY = import.meta.env.VITE_MUX_LICENSE;
if (!MUX_LICENSE_KEY) {
    throw new Error("MUX_LICENSE_KEY is not defined.");
}
LicenseInfo.setLicenseKey(MUX_LICENSE_KEY);

const PUBLISHABLE_KEY = import.meta.env.VITE_PUBLIC_CLERK_PUBLISHABLE_KEY;
if (!PUBLISHABLE_KEY) {
    throw new Error("PUBLISHABLE_KEY is not defined.");
}

export const useStyles = makeStyles({
    root: {
        height: "100%"
    }
});

const partialTheme: PartialTheme = {};
initializeIcons();

// Initialize Google Analytics
initGA();

// Initialize attribution tracking (captures UTM params and gclid)
initAttributionTracking();

// Check for version changes BEFORE rendering the app
// This ensures stale data is cleared before React loads any components
checkAndHandleVersionChange().then(async (versionChanged) => {
    if (versionChanged) {
        console.log('🔄 Version changed - purging persisted state and reloading...');

        // Show splash screen while updating
        const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
        root.render(<VersionUpdateSplash />);

        // Purge the persisted Redux state
        await persistor.purge();

        // Small delay to ensure splash screen is visible
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Reload the page to ensure completely fresh state
        window.location.reload();
        return; // Don't render the app, just reload
    }

    // Version is current - render the app normally
    function App() {
        const styles = useStyles();
        return (
            <React.StrictMode>
                <Provider store={store}>
                    <PersistGate loading={null} persistor={persistor}>
                        <FeatureFlagProvider>
                            <FluentProvider className={styles.root} theme={partialTheme}>
                                <BrowserRouter basename="/">
                                    <ClerkProvider publishableKey={PUBLISHABLE_KEY}>
                                        <AuthProvider>
                                            <AccessManifestProvider>
                                                <AppRoutes />
                                            </AccessManifestProvider>
                                        </AuthProvider>
                                    </ClerkProvider>
                                </BrowserRouter>
                            </FluentProvider>
                        </FeatureFlagProvider>
                    </PersistGate>
                </Provider>
            </React.StrictMode>
        );
    }

    ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(<App />);
});
